/**
 * Heatmap — entity × dimension matrix.
 *
 *   <Heatmap
 *     entities=[{id, name}]
 *     dimensions=[{key, label, formatter?(value)}]
 *     data={ entityId: { dimensionKey: { value, level: 'critical'|'high'|'medium'|'low'|'ok' } } }
 *     onCellClick={(entity, dimension, cell) => void}
 *   />
 *
 * Severity colours come from the design tokens. Cells without data render
 * as a quiet "—".
 */

const LEVEL_CLS = {
    critical: "bg-red-100 text-red-900 border border-red-200",
    high: "bg-orange-100 text-orange-900 border border-orange-200",
    medium: "bg-amber-50 text-amber-900 border border-amber-100",
    low: "bg-lime-50 text-lime-900 border border-lime-100",
    ok: "bg-emerald-50 text-emerald-900 border border-emerald-100",
    none: "bg-stone-50 text-stone-400 border border-stone-100",
};

export function Heatmap({
    entities = [],
    dimensions = [],
    data = {},
    onCellClick,
    testId,
}) {
    return (
        <div
            className="rounded-lg border border-stone-100 overflow-hidden bg-white"
            data-testid={testId}
        >
            <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                    <thead>
                        <tr className="bg-stone-50/60">
                            <th className="sticky left-0 bg-stone-50/60 text-left font-medium text-stone-500 py-3 px-4 text-[11px] uppercase tracking-wider w-44">
                                Entity
                            </th>
                            {dimensions.map((d) => (
                                <th
                                    key={d.key}
                                    className="text-left font-medium text-stone-500 py-3 px-3 text-[11px] uppercase tracking-wider"
                                >
                                    {d.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {entities.map((e) => (
                            <tr
                                key={e.id}
                                className="border-t border-stone-100 hover:bg-stone-50/40"
                            >
                                <th className="sticky left-0 bg-white hover:bg-stone-50/40 text-left font-medium text-stone-800 py-3 px-4 text-[13px] w-44">
                                    {e.name}
                                </th>
                                {dimensions.map((d) => {
                                    const cell = data?.[e.id]?.[d.key];
                                    const level = cell?.level || "none";
                                    const valueDisplay = cell
                                        ? d.formatter
                                            ? d.formatter(cell.value, cell)
                                            : cell.value ?? "—"
                                        : "—";
                                    const Tag = onCellClick ? "button" : "div";
                                    return (
                                        <td key={d.key} className="py-2 px-2 align-middle">
                                            <Tag
                                                type={onCellClick ? "button" : undefined}
                                                onClick={
                                                    onCellClick
                                                        ? () => onCellClick(e, d, cell)
                                                        : undefined
                                                }
                                                className={`w-full inline-flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 ${
                                                    LEVEL_CLS[level] || LEVEL_CLS.none
                                                } ${
                                                    onCellClick && cell
                                                        ? "hover:opacity-90 transition"
                                                        : ""
                                                }`}
                                                data-testid={`heatmap-cell-${e.id}-${d.key}`}
                                                disabled={!cell}
                                            >
                                                <span className="font-mono-num text-[12.5px] tabular-nums">
                                                    {valueDisplay}
                                                </span>
                                            </Tag>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center gap-4 px-4 py-3 border-t border-stone-100 text-[11px] text-stone-500">
                <span className="uppercase tracking-wider font-medium">Legend</span>
                <Swatch level="ok" label="Healthy" />
                <Swatch level="low" label="Watch" />
                <Swatch level="medium" label="Caution" />
                <Swatch level="high" label="Stretch" />
                <Swatch level="critical" label="Critical" />
            </div>
        </div>
    );
}

function Swatch({ level, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded ${LEVEL_CLS[level]}`} />
            <span>{label}</span>
        </span>
    );
}
